import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Bell, Check, Database, Sparkles, Trash2, UserRound } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { scheduleConnectionEmbeddingIndex } from '../../lib/connectionEmbeddings';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import TranscriptReviewTaskPanel from './TranscriptReviewTaskPanel';

export type TranscriptViewerData = {
  id: string;
  title: string;
  content: string;
  summary: string | null;
  meeting_date: string;
  participants: string[] | null;
  /** Q&A requires 'ready' (search index built). */
  transcript_chunks_status?: string | null;
};

/** When opened from a subscriber, show ICP / call-me-when editors without leaving chat. */
export type TranscriptViewerLinkedSubscriber = {
  id: string;
  icp?: string | null;
  callMeWhen1?: string | null;
  callMeWhen2?: string | null;
  callMeWhen3?: string | null;
};

export type TranscriptLinkedSubscriberSavedPatch = Partial<
  Pick<TranscriptViewerLinkedSubscriber, 'icp' | 'callMeWhen1' | 'callMeWhen2' | 'callMeWhen3'>
>;

/** @deprecated Use linkedSubscriber */
export type TranscriptViewerLinkedConnection = {
  id: number;
  icp?: string | null;
  callMeWhen1?: string | null;
  callMeWhen2?: string | null;
  callMeWhen3?: string | null;
};

type ContactEditTarget = 'icp' | 1 | 2 | 3;

export type TranscriptLinkedConnectionSavedPatch = Partial<
  Pick<TranscriptViewerLinkedConnection, 'icp' | 'callMeWhen1' | 'callMeWhen2' | 'callMeWhen3'>
>;

interface TranscriptViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  transcript: TranscriptViewerData | null;
  /** Refetch transcript list so status updates after indexing. */
  onIndexSuccess?: () => void;
  linkedConnection?: TranscriptViewerLinkedConnection | null;
  /** Merge into parent connection state (avoid full reload / loading flash). */
  onLinkedConnectionSaved?: (patch: TranscriptLinkedConnectionSavedPatch) => void;
  linkedSubscriber?: TranscriptViewerLinkedSubscriber | null;
  onLinkedSubscriberSaved?: (patch: TranscriptLinkedSubscriberSavedPatch) => void;
}

type ChatMessage = { id: string; role: 'user' | 'assistant'; content: string };

function formatMeetingDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function contactFieldFilled(v: string | null | undefined): boolean {
  return Boolean(v?.trim());
}

const TranscriptViewerModal: React.FC<TranscriptViewerModalProps> = ({
  isOpen,
  onClose,
  transcript,
  onIndexSuccess,
  linkedConnection,
  onLinkedConnectionSaved,
  linkedSubscriber,
  onLinkedSubscriberSaved,
}) => {
  const { user } = useAuth();
  const profileLink = linkedSubscriber ?? linkedConnection;
  const [prompt, setPrompt] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [asking, setAsking] = useState(false);
  const [indexLoading, setIndexLoading] = useState(false);
  const [contactEdit, setContactEdit] = useState<ContactEditTarget | null>(null);
  const [contactDraft, setContactDraft] = useState('');
  const [contactSaving, setContactSaving] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  /** `${connectionId}:${field}` — only re-seed textarea when switching which field is open (stable across parent re-renders). */
  const contactDraftSeedKey = useRef('');

  useEffect(() => {
    if (!isOpen || !transcript?.id) {
      setChatMessages([]);
      setPrompt('');
      return;
    }

    if (!user?.id) {
      setChatMessages([]);
      setPrompt('');
      return;
    }

    let cancelled = false;
    setChatLoading(true);
    void (async () => {
      try {
        const { data, error } = await supabase
          .from('transcript_chat_messages')
          .select('id, role, content')
          .eq('transcript_id', transcript.id)
          .eq('user_id', user.id)
          .order('created_at', { ascending: true });

        if (error) throw error;
        if (cancelled) return;
        setChatMessages(
          (data ?? []).map((row) => ({
            id: row.id,
            role: row.role as 'user' | 'assistant',
            content: row.content,
          }))
        );
        setPrompt('');
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Could not load chat';
          toast.error(msg);
          setChatMessages([]);
        }
      } finally {
        if (!cancelled) setChatLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, transcript?.id, user?.id]);

  useEffect(() => {
    if (!isOpen) {
      setContactEdit(null);
      setContactDraft('');
      contactDraftSeedKey.current = '';
    }
  }, [isOpen]);

  useEffect(() => {
    if (!profileLink) {
      setContactEdit(null);
      setContactDraft('');
      contactDraftSeedKey.current = '';
    }
  }, [profileLink, linkedSubscriber?.id, linkedConnection?.id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, asking]);

  useEffect(() => {
    const handleEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || !isOpen) return;
      if (contactEdit !== null) {
        setContactEdit(null);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, contactEdit]);

  const sendPrompt = async () => {
    if (!transcript?.id) return;
    if (!user?.id) {
      toast.error('Sign in to use chat');
      return;
    }
    const p = prompt.trim();
    if (!p) {
      toast.error('Enter a message first');
      return;
    }

    const prior = chatMessages;
    const historyPayload = prior.map(({ role, content }) => ({ role, content }));

    const { data: userRow, error: insertUserErr } = await supabase
      .from('transcript_chat_messages')
      .insert({
        transcript_id: transcript.id,
        user_id: user.id,
        role: 'user',
        content: p,
      })
      .select('id, role, content')
      .single();

    if (insertUserErr || !userRow) {
      toast.error(insertUserErr?.message ?? 'Could not save message');
      return;
    }

    const savedUserId = userRow.id as string;
    setChatMessages([...prior, { id: savedUserId, role: 'user', content: p }]);
    setPrompt('');
    setAsking(true);

    try {
      const { data, error } = await supabase.functions.invoke<{
        reply?: string;
        error?: string;
        retrieval?: 'vector';
        transcript_chunks_status?: string;
      }>('transcript-prompt', {
        body: {
          transcript_id: transcript.id,
          prompt: p,
          history: historyPayload,
        },
      });
      if (error) {
        throw new Error(error.message || 'Request failed');
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      if (!data?.reply) {
        throw new Error('No reply from assistant');
      }

      const { data: asstRow, error: insertAsstErr } = await supabase
        .from('transcript_chat_messages')
        .insert({
          transcript_id: transcript.id,
          user_id: user.id,
          role: 'assistant',
          content: data.reply,
        })
        .select('id, role, content')
        .single();

      if (insertAsstErr || !asstRow) {
        throw new Error(insertAsstErr?.message ?? 'Could not save reply');
      }

      setChatMessages((prev) => [
        ...prev,
        { id: asstRow.id as string, role: 'assistant', content: asstRow.content as string },
      ]);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Something went wrong';
      toast.error(message);
      await supabase.from('transcript_chat_messages').delete().eq('id', savedUserId);
      setChatMessages(prior);
      setPrompt(p);
    } finally {
      setAsking(false);
    }
  };

  const openContactEdit = (target: ContactEditTarget) => {
    if (!profileLink) return;
    setContactEdit((prev) => {
      if (prev === target) return null;
      return target;
    });
  };

  useEffect(() => {
    if (!profileLink || contactEdit === null) {
      contactDraftSeedKey.current = '';
      return;
    }
    const seedKey = `${linkedSubscriber?.id ?? linkedConnection?.id}:${contactEdit}`;
    if (contactDraftSeedKey.current === seedKey) return;
    contactDraftSeedKey.current = seedKey;
    if (contactEdit === 'icp') {
      setContactDraft((linkedSubscriber?.icp ?? linkedConnection?.icp) ?? '');
    } else {
      const k = `callMeWhen${contactEdit}` as 'callMeWhen1' | 'callMeWhen2' | 'callMeWhen3';
      setContactDraft(String((linkedSubscriber?.[k] ?? linkedConnection?.[k]) ?? ''));
    }
  }, [contactEdit, profileLink, linkedSubscriber, linkedConnection]);

  const saveContactField = async () => {
    if (!profileLink || contactEdit === null) return;
    setContactSaving(true);
    try {
      const updatedAt = new Date().toISOString();
      const trimmedVal = contactDraft.trim() || null;

      if (linkedSubscriber) {
        const customKey =
          contactEdit === 'icp'
            ? 'icp'
            : (`call_me_when_${contactEdit}` as 'call_me_when_1');
        const { data: existing } = await supabase
          .from('subscribers')
          .select('custom_data')
          .eq('id', linkedSubscriber.id)
          .maybeSingle();
        const customData = {
          ...((existing?.custom_data as Record<string, unknown>) ?? {}),
          [customKey]: trimmedVal,
        };
        const { error } = await supabase
          .from('subscribers')
          .update({ custom_data: customData, updated_at: updatedAt })
          .eq('id', linkedSubscriber.id);
        if (error) throw error;
        toast.success(contactEdit === 'icp' ? 'ICP saved' : 'Call me when saved');
        const patch =
          contactEdit === 'icp'
            ? { icp: trimmedVal }
            : contactEdit === 1
              ? { callMeWhen1: trimmedVal }
              : contactEdit === 2
                ? { callMeWhen2: trimmedVal }
                : { callMeWhen3: trimmedVal };
        onLinkedSubscriberSaved?.(patch);
      } else if (linkedConnection) {
        if (contactEdit === 'icp') {
          const { error } = await supabase
            .from('connections')
            .update({
              icp: trimmedVal,
              updated_at: updatedAt,
            })
            .eq('id', linkedConnection.id);
          if (error) throw error;
          toast.success('ICP saved');
        } else {
          const key = `callMeWhen${contactEdit}` as 'callMeWhen1' | 'callMeWhen2' | 'callMeWhen3';
          const { error } = await supabase
            .from('connections')
            .update({
              [key]: trimmedVal,
              updated_at: updatedAt,
            })
            .eq('id', linkedConnection.id);
          if (error) throw error;
          toast.success('Call me when saved');
        }
        scheduleConnectionEmbeddingIndex(linkedConnection.id);
        if (contactEdit === 'icp') {
          onLinkedConnectionSaved?.({ icp: trimmedVal });
        } else if (contactEdit === 1) {
          onLinkedConnectionSaved?.({ callMeWhen1: trimmedVal });
        } else if (contactEdit === 2) {
          onLinkedConnectionSaved?.({ callMeWhen2: trimmedVal });
        } else {
          onLinkedConnectionSaved?.({ callMeWhen3: trimmedVal });
        }
      }
      setContactEdit(null);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setContactSaving(false);
    }
  };

  const clearChat = async () => {
    if (!transcript?.id || !user?.id) {
      setChatMessages([]);
      return;
    }
    const { error } = await supabase
      .from('transcript_chat_messages')
      .delete()
      .eq('transcript_id', transcript.id)
      .eq('user_id', user.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setChatMessages([]);
  };

  const buildSearchIndex = async () => {
    if (!transcript?.id) return;
    setIndexLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        chunkCount?: number;
        error?: string;
      }>('index-transcript-chunks', { body: { transcript_id: transcript.id } });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success(`Indexed ${data?.chunkCount ?? 0} chunk(s)`);
      onIndexSuccess?.();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Index failed');
    } finally {
      setIndexLoading(false);
    }
  };

  if (!isOpen || !transcript) return null;

  const indexReady = transcript.transcript_chunks_status === 'ready';

  return (
    <div className="fixed inset-0 z-50 flex">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 dark:bg-black/60"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="relative ml-auto flex h-full w-full max-w-[min(100vw,90rem)] flex-col bg-white shadow-xl dark:bg-gray-900 lg:flex-row min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="transcript-viewer-title"
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col border-b border-gray-200 dark:border-gray-700 lg:border-b-0 lg:border-r">
          <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1">
              <h2 id="transcript-viewer-title" className="truncate text-lg font-semibold text-gray-900 dark:text-white">
                {transcript.title}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{formatMeetingDate(transcript.meeting_date)}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={() => void buildSearchIndex()}
                disabled={indexLoading}
                className="inline-flex items-center gap-1 rounded-lg border border-violet-300 px-2 py-1.5 text-xs font-medium text-violet-800 hover:bg-violet-50 disabled:opacity-50 dark:border-violet-700 dark:text-violet-200 dark:hover:bg-violet-950/40"
                title="Build vector chunks for Q&A (does not change transcript text)"
              >
                <Database className="h-3.5 w-3.5" />
                {indexLoading ? 'Indexing…' : 'Index'}
              </button>
            </div>
          </div>

          <TranscriptReviewTaskPanel transcriptId={transcript.id} compact />

          {profileLink ? (
            <div className="shrink-0 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap items-center gap-2 px-4 py-2">
                <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Contact
                </span>
                <button
                  type="button"
                  onClick={() => openContactEdit('icp')}
                  className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${
                    contactEdit === 'icp'
                      ? 'border-violet-500 bg-violet-50 text-violet-900 dark:border-violet-400 dark:bg-violet-950/50 dark:text-violet-100'
                      : contactFieldFilled(linkedSubscriber?.icp ?? linkedConnection?.icp)
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100/80 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-950/55'
                        : 'border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                  title={
                    contactFieldFilled(linkedSubscriber?.icp ?? linkedConnection?.icp)
                      ? 'ICP defined — click to edit'
                      : 'ICP empty — click to add'
                  }
                >
                  {contactEdit !== 'icp' && contactFieldFilled(linkedSubscriber?.icp ?? linkedConnection?.icp) ? (
                    <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
                  ) : (
                    <UserRound className="h-3.5 w-3.5 shrink-0 opacity-80" />
                  )}
                  ICP
                </button>
                {([1, 2, 3] as const).map((n) => {
                  const k = `callMeWhen${n}` as 'callMeWhen1' | 'callMeWhen2' | 'callMeWhen3';
                  const val = linkedSubscriber?.[k] ?? linkedConnection?.[k];
                  const filled = contactFieldFilled(String(val ?? ''));
                  return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => openContactEdit(n)}
                    className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${
                      contactEdit === n
                        ? 'border-amber-500 bg-amber-50 text-amber-950 dark:border-amber-400 dark:bg-amber-950/40 dark:text-amber-100'
                        : filled
                          ? 'border-amber-200 bg-amber-50/90 text-amber-950 hover:bg-amber-100/90 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100 dark:hover:bg-amber-950/50'
                          : 'border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800'
                    }`}
                    title={
                      filled
                        ? `Call me when #${n} defined — click to edit`
                        : `Call me when #${n} empty — click to add`
                    }
                  >
                    {contactEdit !== n && filled ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400" strokeWidth={2.5} />
                    ) : (
                      <Bell className="h-3.5 w-3.5 shrink-0 opacity-80" />
                    )}
                    When {n}
                  </button>
                  );
                })}
              </div>
              {contactEdit !== null ? (
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/60">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                    {contactEdit === 'icp' ? 'Ideal customer profile' : `Call me when — statement ${contactEdit}`}
                  </label>
                  <textarea
                    value={contactDraft}
                    onChange={(e) => setContactDraft(e.target.value)}
                    rows={contactEdit === 'icp' ? 5 : 4}
                    className="mt-2 w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    disabled={contactSaving}
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void saveContactField()}
                      disabled={contactSaving}
                      className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                    >
                      {contactSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setContactEdit(null)}
                      disabled={contactSaving}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {transcript.participants && transcript.participants.length > 0 ? (
              <div className="mb-4 flex flex-wrap gap-1.5">
                {transcript.participants.map((p, i) => (
                  <span
                    key={`${i}-${p.slice(0, 24)}`}
                    className="inline-block max-w-full truncate rounded-full bg-violet-100 px-2.5 py-0.5 text-xs text-violet-900 dark:bg-violet-900/40 dark:text-violet-100"
                    title={p}
                  >
                    {p}
                  </span>
                ))}
              </div>
            ) : null}
            {transcript.summary?.trim() ? (
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800/80">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Summary</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">{transcript.summary.trim()}</p>
              </div>
            ) : null}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Transcript</p>
              <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-gray-900 dark:text-gray-100">
                {transcript.content || '—'}
              </pre>
            </div>
          </div>
        </div>

        <aside className="flex max-h-screen min-h-0 w-full shrink-0 flex-col border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-950 lg:max-h-none lg:w-[min(100%,32rem)] lg:border-l lg:border-t-0 xl:w-[36rem] 2xl:w-[40rem]">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="h-6 w-6 shrink-0 text-violet-600 dark:text-violet-400" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">Chat about this transcript</h3>
            </div>
            {chatMessages.length > 0 ? (
              <button
                type="button"
                onClick={() => void clearChat()}
                disabled={chatLoading}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800 disabled:opacity-50"
                title="Clear conversation for this transcript"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Clear
              </button>
            ) : null}
          </div>

          {!indexReady ? (
            <div className="shrink-0 border-b border-amber-100 bg-amber-50 px-5 py-3 text-sm leading-snug text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
              Q&amp;A is disabled until the search index is built. Use <strong>Index</strong> above, then try again.
              {transcript.transcript_chunks_status ? (
                <span className="block mt-1.5 opacity-90">
                  Current status: <code className="text-sm font-semibold">{transcript.transcript_chunks_status}</code>
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {chatLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading chat…</p>
            ) : chatMessages.length === 0 ? (
              <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
                Messages are saved for you on this transcript (same history from Contacts or Transcripts). Each send includes the
                thread so far. Answers use this meeting&apos;s search index, not the open web.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                {chatMessages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[95%] rounded-2xl px-4 py-3 text-base leading-relaxed ${
                        m.role === 'user'
                          ? 'bg-violet-600 text-white'
                          : 'border border-gray-200 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100'
                      }`}
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80 mb-1.5">
                        {m.role === 'user' ? 'You' : 'Assistant'}
                      </p>
                      <pre className="whitespace-pre-wrap break-words font-sans">{m.content}</pre>
                    </div>
                  </div>
                ))}
                {asking ? (
                  <div className="flex justify-start">
                    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400">
                      Thinking…
                    </div>
                  </div>
                ) : null}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-950">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400" htmlFor="transcript-prompt-input">
              Message
            </label>
            <textarea
              id="transcript-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  if (!asking && indexReady && prompt.trim()) void sendPrompt();
                }
              }}
              rows={3}
              placeholder="Ask a question… (⌘/Ctrl+Enter to send)"
              className="mt-2 min-h-[4.5rem] w-full resize-y rounded-lg border border-gray-300 bg-white px-3.5 py-3 text-base leading-relaxed text-gray-900 shadow-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-white disabled:opacity-50"
              disabled={asking || !indexReady || chatLoading}
            />
            <button
              type="button"
              onClick={() => void sendPrompt()}
              disabled={asking || !prompt.trim() || !indexReady || chatLoading}
              className="mt-3 w-full rounded-lg bg-violet-600 px-4 py-3 text-base font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {asking ? 'Sending…' : 'Send'}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default TranscriptViewerModal;
